import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import ResizableImage from '../components/ResizableImageExtension';
import EditorToolbar from '../components/EditorToolbar';
import api from '../utils/api';
import { BookOpen, Plus, X, Search } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LottieAnim from '../components/LottieAnim';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

function LogEditor({ categories, onSave, onCancel }) {
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
    ],
    editorProps: {
      attributes: { class: 'tiptap !min-h-[200px] !p-3' },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
      // 이미지 파일을 본문에 드롭하면 그 자리에 업로드해서 삽입한다.
      // moved=true 는 에디터 안의 노드를 옮기는 것이므로 기본 동작에 맡긴다.
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const files = Array.from(event.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        setDragOver(false);
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        let pos = coords ? coords.pos : view.state.selection.from;
        (async () => {
          for (const f of files) {
            setUploading(n => n + 1);
            try {
              const url = await uploadImage(f);
              const node = view.state.schema.nodes.image.create({ src: url });
              // 앞 이미지가 들어가며 문서가 길어지므로 매번 현재 문서 기준으로 자른다
              const at = Math.min(pos, view.state.doc.content.size);
              view.dispatch(view.state.tr.insert(at, node));
              pos = at + node.nodeSize;
            } catch {
              /* 업로드 실패한 파일은 건너뛴다 */
            } finally {
              setUploading(n => n - 1);
            }
          }
        })();
        return true;
      },
    },
  });

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const save = () => {
    if (!title.trim()) return;
    onSave({ title: title.trim(), content: editor?.getHTML() || '', categoryId: categoryId || null });
  };

  return (
    <div className="bg-gray-900 border border-brand/30 rounded-xl mb-4">
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center gap-3 border-b border-gray-800 pb-3 mb-3">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="제목을 입력하세요"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && editor?.commands.focus()}
            className="flex-1 bg-transparent text-white text-base font-semibold focus:outline-none placeholder-gray-600"
          />
          {categories.length > 0 && (
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-gray-300 text-xs focus:outline-none focus:border-brand"
            >
              <option value="">카테고리 없음</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
        <EditorToolbar editor={editor} onImageUpload={() => fileInputRef.current?.click()} />
        <div
          onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
          onDrop={() => setDragOver(false)}
          className={`relative border rounded-lg mt-2 overflow-hidden transition-colors ${
            dragOver ? 'border-brand bg-brand/5' : 'border-gray-800'
          }`}>
          <EditorContent editor={editor} />
          {dragOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-brand/10 pointer-events-none">
              <span className="text-brand text-sm font-medium">이미지를 여기에 놓으세요</span>
            </div>
          )}
          {uploading > 0 && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-gray-900/90 border border-gray-700 rounded-lg px-2.5 py-1 pointer-events-none">
              <span className="w-3 h-3 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              <span className="text-gray-300 text-xs">이미지 업로드 중 {uploading}</span>
            </div>
          )}
        </div>
        <p className="text-[11px] text-gray-600 mt-1.5">이미지는 드래그해서 놓거나 붙여넣기로 넣을 수 있습니다.</p>
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
      </div>
      <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
        <button onClick={onCancel} className="px-4 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
        <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">저장</button>
      </div>
    </div>
  );
}

const LIMIT = 20;

export default function ResearchLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();

  const loadCategories = () => api.get('/research-logs/categories').then(r => setCategories(r.data));

  const load = useCallback(async (p = 1) => {
    const params = { page: p, limit: LIMIT };
    if (selectedCatId !== 'all') params.categoryId = selectedCatId;
    if (search.trim()) params.search = search.trim();
    const r = await api.get('/research-logs', { params });
    setLogs(r.data.logs);
    setTotal(r.data.total);
  }, [selectedCatId, search]);

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { load(page); }, [load, page]);

  const doSearch = () => { setSearch(searchInput); setPage(1); };

  const save = async ({ title, content, categoryId }) => {
    await api.post('/research-logs', { title, content, categoryId });
    setShowForm(false);
    setPage(1);
    load(1);
  };

  const selectCat = (id) => { setSelectedCatId(id); setPage(1); };

  const fmt = (d) => new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <PageHeader icon={BookOpen} title="기록원" count={total}>
        <button onClick={() => setShowForm(s => !s)}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors ${
            showForm ? 'bg-gray-700 text-gray-300' : 'bg-brand text-white hover:bg-brand/80'
          }`}>
          {showForm ? <><X size={14} /> 취소</> : <><Plus size={14} /> 작성</>}
        </button>
      </PageHeader>

      {/* 카테고리 탭 + 검색 */}
      <div className="flex items-center justify-between mb-4 gap-3">
        {categories.length > 0 ? (
          <div className="flex items-end gap-0 overflow-x-auto scrollbar-none border-b border-gray-800 flex-1">
            {[{ id: 'all', name: '전체' }, ...categories].map(c => (
              <button key={c.id} onClick={() => selectCat(String(c.id))}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex-shrink-0 ${
                  selectedCatId === String(c.id) ? 'text-brand border-brand' : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
                }`}>
                {c.name}
              </button>
            ))}
          </div>
        ) : <div className="flex-1" />}

        <div className="flex items-center gap-1 flex-shrink-0">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="검색..."
            className="w-36 bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-brand placeholder-gray-600"
          />
          <button onClick={doSearch} className="p-1.5 text-gray-500 hover:text-white">
            <Search size={14} />
          </button>
        </div>
      </div>

      {showForm && (
        <LogEditor categories={categories} onSave={save} onCancel={() => setShowForm(false)} />
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className={`hidden sm:grid gap-2 px-4 py-2 border-b border-gray-800 text-gray-600 text-xs ${categories.length > 0 ? 'grid-cols-[3rem_1fr_7rem_7rem_7rem]' : 'grid-cols-[3rem_1fr_7rem_7rem]'}`}>
          <span className="text-center">번호</span>
          <span>제목</span>
          {categories.length > 0 && <span>카테고리</span>}
          <span>작성자</span>
          <span className="text-right">작성일</span>
        </div>

        {logs.length === 0 ? (
          <div className="flex flex-col items-center py-10">
            <LottieAnim type="empty" size={100} />
            <p className="text-gray-600 text-sm mt-2">기록이 없습니다.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-800">
            {logs.map((log, idx) => (
              <li key={log.id}
                onClick={() => navigate(`/research/${log.id}`)}
                className={`grid grid-cols-[3rem_1fr] gap-2 items-center px-4 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors ${categories.length > 0 ? 'sm:grid-cols-[3rem_1fr_7rem_7rem_7rem]' : 'sm:grid-cols-[3rem_1fr_7rem_7rem]'}`}>
                <span className="text-xs text-gray-600 text-center">{total - (page - 1) * LIMIT - idx}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-200 truncate">{log.title}</p>
                  <p className="sm:hidden text-xs text-gray-600 mt-0.5">
                    {log.author?.name || log.author?.username} · {fmt(log.createdAt)}
                  </p>
                </div>
                {categories.length > 0 && (
                  <span className="hidden sm:block text-xs">
                    {log.category
                      ? <span className="px-2 py-0.5 rounded-full" style={{ background: log.category.color + '33', color: log.category.color }}>{log.category.name}</span>
                      : <span className="text-gray-600">-</span>}
                  </span>
                )}
                <span className="hidden sm:block text-xs text-gray-500 truncate">{log.author?.name || log.author?.username}</span>
                <span className="hidden sm:block text-xs text-gray-500 text-right">{fmt(log.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className={`w-8 h-8 text-sm rounded-md ${
                p === page ? 'bg-brand text-white' : 'text-gray-500 hover:text-white hover:bg-gray-800'
              }`}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
